import type { FormData } from "../types";

export interface ValidationError {
	field: string;
	message: string;
}

export interface ValidationResult {
	isValid: boolean;
	errors: ValidationError[];
}

/**
 * Check if a string contains only ASCII characters
 */
const isAscii = (str: string): boolean => {
	// biome-ignore lint/suspicious/noControlCharactersInRegex: ASCII range check requires control characters
	return /^[\x00-\x7F]*$/.test(str);
};

/**
 * Client-side validation that matches server-side validation
 * This prevents users from submitting forms that will fail on the server
 */
export const validateDonationForm = (formData: FormData): ValidationResult => {
	const errors: ValidationError[] = [];

	// Validate first name
	if (!formData.firstName.trim()) {
		errors.push({ field: "firstName", message: "First name is required" });
	} else if (formData.firstName.length > 100) {
		errors.push({
			field: "firstName",
			message: "First name must be 100 characters or less",
		});
	} else if (!isAscii(formData.firstName)) {
		errors.push({
			field: "firstName",
			message: "First name must contain only ASCII characters",
		});
	}

	// Validate last name
	if (!formData.lastName.trim()) {
		errors.push({ field: "lastName", message: "Last name is required" });
	} else if (formData.lastName.length > 100) {
		errors.push({
			field: "lastName",
			message: "Last name must be 100 characters or less",
		});
	} else if (!isAscii(formData.lastName)) {
		errors.push({
			field: "lastName",
			message: "Last name must contain only ASCII characters",
		});
	}

	// Validate email
	if (!formData.email.trim()) {
		errors.push({ field: "email", message: "Email is required" });
	} else {
		if (formData.email.length > 254) {
			errors.push({ field: "email", message: "Email address is too long" });
		} else if (!isAscii(formData.email)) {
			errors.push({
				field: "email",
				message: "Email must contain only ASCII characters",
			});
		} else {
			const emailRegex = /^[^@]+@[^@]+\.[^@]+$/;
			if (!emailRegex.test(formData.email)) {
				errors.push({
					field: "email",
					message: "Please enter a valid email address",
				});
			}
		}
	}

	// Validate country
	if (!formData.country.trim()) {
		errors.push({ field: "country", message: "Country is required" });
	} else if (formData.country.length > 100) {
		errors.push({
			field: "country",
			message: "Country must be 100 characters or less",
		});
	} else if (!isAscii(formData.country)) {
		errors.push({
			field: "country",
			message: "Country must contain only ASCII characters",
		});
	}

	// Validate payment method
	if (!formData.paymentMethod) {
		errors.push({
			field: "paymentMethod",
			message: "Payment method is required",
		});
	}

	// Validate donation amount (in cents)
	const totalDonationCents =
		formData.allocationType === "specific"
			? Object.values(formData.specificAllocationsCents).reduce(
					(sum, amount) => sum + amount,
					0,
				)
			: formData.basicDonationAmountCents;

	if (totalDonationCents < 400) {
		// $4 HKD = 400 cents
		errors.push({
			field: "donationAmount",
			message: "Minimum donation amount is $4 HKD",
		});
	}

	if (!formData.howDidYouHearAboutUs) {
		errors.push({
			field: "howDidYouHearAboutUs",
			message: "How did you hear about us is required",
		});
	} else {
		if (formData.howDidYouHearAboutUs.length > 100) {
			errors.push({
				field: "howDidYouHearAboutUs",
				message: "How did you hear about us must be 100 characters or less",
			});
		}
	}

	// Validate recurring + bank transfer combination
	if (formData.recurring && formData.paymentMethod === "bank-transfer") {
		errors.push({
			field: "paymentMethod",
			message:
				"Bank transfers cannot be recurring. Please use credit card for monthly donations.",
		});
	}

	return {
		isValid: errors.length === 0,
		errors,
	};
};

/**
 * Validate individual field in real-time
 */
export const validateField = (
	fieldName: keyof FormData,
	value: FormData[keyof FormData],
): ValidationError | null => {
	switch (fieldName) {
		case "firstName":
			if (typeof value !== "string") return null;
			if (!value.trim()) {
				return { field: fieldName, message: "First name is required" };
			}
			if (value.length > 100) {
				return {
					field: fieldName,
					message: "First name must be 100 characters or less",
				};
			}
			if (!isAscii(value)) {
				return {
					field: fieldName,
					message: "First name must contain only ASCII characters",
				};
			}
			break;

		case "lastName":
			if (typeof value !== "string") return null;
			if (!value.trim()) {
				return { field: fieldName, message: "Last name is required" };
			}
			if (value.length > 100) {
				return {
					field: fieldName,
					message: "Last name must be 100 characters or less",
				};
			}
			if (!isAscii(value)) {
				return {
					field: fieldName,
					message: "Last name must contain only ASCII characters",
				};
			}
			break;

		case "email": {
			if (typeof value !== "string") return null;
			if (!value.trim()) {
				return { field: fieldName, message: "Email is required" };
			}
			if (value.length > 254) {
				return { field: fieldName, message: "Email address is too long" };
			}
			if (!isAscii(value)) {
				return {
					field: fieldName,
					message: "Email must contain only ASCII characters",
				};
			}
			const emailRegex = /^[^@]+@[^@]+\.[^@]+$/;
			if (!emailRegex.test(value)) {
				return {
					field: fieldName,
					message: "Please enter a valid email address",
				};
			}
			break;
		}

		case "country":
			if (typeof value !== "string") return null;
			if (!value.trim()) {
				return { field: fieldName, message: "Country is required" };
			}
			if (value.length > 100) {
				return {
					field: fieldName,
					message: "Country must be 100 characters or less",
				};
			}
			if (!isAscii(value)) {
				return {
					field: fieldName,
					message: "Country must contain only ASCII characters",
				};
			}
			break;

		case "howDidYouHearAboutUs":
			if (typeof value !== "string") return null;
			if (value.length > 100) {
				return {
					field: fieldName,
					message: "How did you hear about us must be 100 characters or less",
				};
			}
			break;
	}

	return null;
};

/**
 * Get user-friendly error message for display
 */
export const getFieldErrorMessage = (
	errors: ValidationError[],
	fieldName: string,
): string | null => {
	const error = errors.find((err) => err.field === fieldName);
	return error ? error.message : null;
};

/**
 * Check if a specific field has validation errors
 */
export const hasFieldError = (
	errors: ValidationError[],
	fieldName: string,
): boolean => {
	return errors.some((err) => err.field === fieldName);
};
